import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Maximize2 } from 'lucide-react';

export default function HomeSciCalc({ replacement }) {
  const [display, setDisplay] = useState('0');
  const [expr, setExpr] = useState('');
  const [deg, setDeg] = useState(true);
  // If a full Scientific Calculator component is passed in, render it directly
  // inside the same styled card. This lets the home page embed the real calc
  // from /calc/scientific rather than a mini clone.
  if (replacement) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="home-scientific-calc">
        {replacement}
      </div>
    );
  }

  const press = (v) => {
    if (display === '0' && /[0-9.]/.test(v)) setDisplay(v);
    else if (display === 'Error') setDisplay(v);
    else setDisplay(display + v);
  };
  const clear = () => { setDisplay('0'); setExpr(''); };
  const back = () => setDisplay(display.length > 1 ? display.slice(0, -1) : '0');
  const evalExpr = () => {
    try {
      let e = display.replace(/×/g, '*').replace(/÷/g, '/').replace(/−/g, '-').replace(/π/g, 'Math.PI').replace(/(?<!Math\.)e(?![a-zA-Z])/g, 'Math.E');
      e = e.replace(/sin\(/g, `Math.sin(${deg ? '(Math.PI/180)*' : ''}`);
      e = e.replace(/cos\(/g, `Math.cos(${deg ? '(Math.PI/180)*' : ''}`);
      e = e.replace(/tan\(/g, `Math.tan(${deg ? '(Math.PI/180)*' : ''}`);
      e = e.replace(/log\(/g, 'Math.log10(');
      e = e.replace(/ln\(/g, 'Math.log(');
      e = e.replace(/sqrt\(/g, 'Math.sqrt(');
      e = e.replace(/\^/g, '**');
      e = e.replace(/(\d+)!/g, (_, n) => { let r = 1; for (let i = 2; i <= +n; i++) r *= i; return r; });
      // eslint-disable-next-line no-new-func
      const r = Function('"use strict"; return (' + e + ')')();
      setExpr(display + ' =');
      setDisplay(String(r));
    } catch { setDisplay('Error'); }
  };

  const Btn = ({ label, onClick, cls }) => (
    <button onClick={onClick || (() => press(label))}
      className={`h-11 sm:h-12 rounded-xl font-semibold text-sm transition-all active:scale-95 ${cls || 'bg-slate-100 hover:bg-slate-200 text-slate-900'}`}>{label}</button>
  );

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Scientific Calculator</h3>
          <p className="text-xs text-slate-500">Quick calculations with trig, log, and exponents</p>
        </div>
        <Link to="/calc/scientific" className="text-blue-600 hover:text-blue-700" title="Open full calculator"><Maximize2 className="w-4 h-4" /></Link>
      </div>
      <div className="p-4">
        <div className="bg-slate-900 text-white rounded-xl p-4 mb-3">
          <div className="text-right text-xs text-slate-400 h-4">{expr}</div>
          <div className="text-right text-3xl font-bold overflow-x-auto whitespace-nowrap">{display}</div>
        </div>
        <div className="flex items-center justify-between mb-2 gap-2">
          <div className="flex gap-1">
            <button onClick={() => setDeg(true)} className={`px-3 py-1 rounded-lg text-xs font-semibold ${deg ? 'bg-blue-600 text-white' : 'bg-slate-100'}`}>DEG</button>
            <button onClick={() => setDeg(false)} className={`px-3 py-1 rounded-lg text-xs font-semibold ${!deg ? 'bg-blue-600 text-white' : 'bg-slate-100'}`}>RAD</button>
          </div>
        </div>
        <div className="grid grid-cols-5 gap-1.5">
          <Btn label="sin(" /><Btn label="cos(" /><Btn label="tan(" /><Btn label="log(" /><Btn label="ln(" />
          <Btn label="π" /><Btn label="sqrt(" /><Btn label="^" /><Btn label="!" /><Btn label="e" />
          <Btn label="(" /><Btn label=")" /><Btn label="%" />
          <Btn label="AC" onClick={clear} cls="bg-red-500 hover:bg-red-600 text-white" />
          <Btn label="⌫" onClick={back} cls="bg-slate-300 hover:bg-slate-400 text-slate-900" />
          <Btn label="7" /><Btn label="8" /><Btn label="9" /><Btn label="÷" onClick={() => press('÷')} /><Btn label="×" onClick={() => press('×')} />
          <Btn label="4" /><Btn label="5" /><Btn label="6" /><Btn label="−" onClick={() => press('-')} /><Btn label="+" />
          <Btn label="1" /><Btn label="2" /><Btn label="3" />
          <Btn label="=" onClick={evalExpr} cls="row-span-2 bg-blue-600 hover:bg-blue-700 text-white" />
          <Btn label="." />
          <Btn label="0" /><Btn label="00" onClick={() => press('00')} />
        </div>
      </div>
    </div>
  );
}
